import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { TableColumn } from '../../../osp-ui/components/data/table/contract/table-column.contract';
import { PageableDatasource } from '../../../shared/contract/pageable-datasource.contract';
import { PageableRequestParams } from '../../../shared/contract/pageable-request-params.contract';
import { PagedResponse } from '../../../shared/contract/paged-response.contract';
import {
  EquipmentTypeFilterView,
} from '../../../shared/filters/contract/filter-view/equipment-type-filter-view.contract';
import { AcknowledgeAlarmCommand } from '../contract/acknowledge-alarm-command.contract';
import { AlarmRequestParams } from '../contract/alarm-request.contract';
import { Alarm } from '../contract/alarm.contract';
import { CreateAlarmRuleCommand } from '../contract/create-alarm-rule-command.contract';
import { Customer } from '../contract/customer.contract';
import { DeleteAlarmRuleCommand } from '../contract/delete-alarm-rule-command.contract';
import { Equipment } from '../contract/equipment.contract';
import { UpdateAlarmRuleCommand } from '../contract/update-alarm-rule-command.contract';
import { ViewAlarmRule } from '../contract/view-alarm-rule.contract';
import { AlarmType } from '../enums/alarm-type.enum';
import { NotificationApiService } from './notification-api.service';
import { NotificationsListConfigService } from './notifications-list-config.service';

enum EntityType {
  TRANSPORT = 'TRANSPORT',
  EQUIPMENT = 'EQUIPMENT',
}

type AlarmTypesMapping = { [key in AlarmType]: EntityType };

type MethodsMapping = {
  [key in EntityType]: {
    create: (command: CreateAlarmRuleCommand) => Observable<string>;
    update: (command: UpdateAlarmRuleCommand) => Observable<string>;
    delete: (command: DeleteAlarmRuleCommand) => Observable<string>;
  };
};

@Injectable({ providedIn: 'root' })
export class NotificationsDatasourceService extends PageableDatasource<ViewAlarmRule> {
  private readonly DEFAULT_OPTIONS_PAGE_SIZE = 300;

  private _currentActive$: BehaviorSubject<Alarm[]> = new BehaviorSubject<Alarm[]>([]);
  private _currentActiveTotalCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private _equipmentTypes$: BehaviorSubject<EquipmentTypeFilterView[]> = new BehaviorSubject<EquipmentTypeFilterView[]>(
    [],
  );
  private _labels$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private _isEquipmentOwner$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly alarmTypesMapping: AlarmTypesMapping = {
    [AlarmType.OPERATING_HOURS]: EntityType.EQUIPMENT,
    [AlarmType.OPERATING_HOURS_CONTRACT]: EntityType.EQUIPMENT,
    [AlarmType.TANK_FILL_LEVEL]: EntityType.EQUIPMENT,
    [AlarmType.FAULTS]: EntityType.EQUIPMENT,
    [AlarmType.TOTAL_COSTS]: EntityType.EQUIPMENT,
    [AlarmType.DAMAGE_COSTS]: EntityType.EQUIPMENT,
    [AlarmType.REPAIR_COSTS]: EntityType.EQUIPMENT,
    [AlarmType.GUARANTEE]: EntityType.EQUIPMENT,
    [AlarmType.FULL_SERVICE_CONTRACT_END]: EntityType.EQUIPMENT,
    [AlarmType.LEASING_SERVICE_CONTRACT_END]: EntityType.EQUIPMENT,
    [AlarmType.GEOFENCE_VIOLATION]: EntityType.EQUIPMENT,
    [AlarmType.GLOBAL_GEOFENCE_VIOLATION]: EntityType.EQUIPMENT,
    [AlarmType.TIMEFENCING]: EntityType.EQUIPMENT,
    [AlarmType.TRANSPORT_TASK_CREATION]: EntityType.TRANSPORT,
    [AlarmType.TRANSPORT_TASK_RETURNED]: EntityType.TRANSPORT,
    [AlarmType.NEW_DAMAGE_ADDED]: EntityType.EQUIPMENT,
    [AlarmType.INACTIVE_TELEMATICS_UNIT]: EntityType.EQUIPMENT,
    [AlarmType.BATTERY_VOLTAGE]: EntityType.EQUIPMENT,
    [AlarmType.DEF_TANK_LEVEL]: EntityType.EQUIPMENT,
    [AlarmType.TELEMATICS_UNIT_BATTERY_STATUS]: EntityType.EQUIPMENT,
    [AlarmType.THEFT_PROTECTION]: EntityType.EQUIPMENT,
  };
  private readonly methodsMapping: MethodsMapping = {
    [EntityType.EQUIPMENT]: {
      create: (command: CreateAlarmRuleCommand) => this.notificationApiService.createEquipmentAlarmRule(command),
      update: (command: UpdateAlarmRuleCommand) => this.notificationApiService.updateEquipmentAlarmRule(command),
      delete: (command: DeleteAlarmRuleCommand) => this.notificationApiService.removeEquipmentAlarmRule(command),
    },
    [EntityType.TRANSPORT]: {
      create: (command: CreateAlarmRuleCommand) => this.notificationApiService.createTransportAlarmRule(command),
      update: (command: UpdateAlarmRuleCommand) => this.notificationApiService.updateTransportAlarmRule(command),
      delete: (command: DeleteAlarmRuleCommand) => this.notificationApiService.removeTransportAlarmRule(command),
    },
  };

  public readonly selectedColumns$ = this.listConfigService.selectedColumns$;
  public readonly contentEmitters = this.listConfigService.contentEmitters;
  public readonly currentActive$: Observable<Alarm[]> = this._currentActive$.asObservable();
  public readonly currentActiveTotalCount$: Observable<number> = this._currentActiveTotalCount$.asObservable();

  constructor(
    protected listConfigService: NotificationsListConfigService,
    protected notificationApiService: NotificationApiService,
  ) {
    super();
    super.changePageSize(this.listConfigService.pageSize, false);
  }

  get availableColumns(): TableColumn<ViewAlarmRule>[] {
    return this.listConfigService.availableColumns;
  }

  public requestData(params: PageableRequestParams): void {
    const requestParams = this.defineRequestParams(params);
    this.notificationApiService.getAlarmRules(requestParams).subscribe((resp) => this.setPageableData(resp));
  }

  public getCurrentActive(): Observable<Alarm[]> {
    this.notificationApiService.getCurrentActive().subscribe((res: PagedResponse<Alarm>) => {
      this._currentActive$.next(res.content);
      this._currentActiveTotalCount$.next(res.totalElements);
    });
    return this._currentActive$;
  }

  public getDistinctEquipmentTypes(): Observable<EquipmentTypeFilterView[]> {
    this.notificationApiService
      .getDistinctEquipmentTypes()
      .subscribe((res: EquipmentTypeFilterView[]) => this._equipmentTypes$.next(res));

    return this._equipmentTypes$;
  }

  public getDistinctLabels(): Observable<string[]> {
    this.notificationApiService
      .getDistinctLabels()
      .subscribe((res: string[]) => this._labels$.next(res));

    return this._labels$;
  }

  public getEquipmentOptions(term: string, excludeShared: boolean): Observable<Equipment[]> {
    return this.notificationApiService
      .getEquipments({ term, page: 0, size: this.DEFAULT_OPTIONS_PAGE_SIZE, excludeShared })
      .pipe(map((res: PagedResponse<Equipment>) => res.content));
  }

  public getCustomersWithSharesOptions(term: string): Observable<Customer[]> {
    return this.notificationApiService
      .getCustomersWithShares({ term, page: 0, size: this.DEFAULT_OPTIONS_PAGE_SIZE })
      .pipe(map((res: PagedResponse<Customer>) => res.content));
  }

  public isEquipmentOwner(): Observable<boolean> {
    this.notificationApiService
      .isEquipmentOwner()
      .subscribe((res: boolean) => this._isEquipmentOwner$.next(res));

    return this._isEquipmentOwner$
  }

  public acknowledgeAlarm(command: AcknowledgeAlarmCommand): Observable<string> {
    return this.notificationApiService.acknowledgeAlarm(command);
  }

  public deleteAlarmRule(command: DeleteAlarmRuleCommand): Observable<string> {
    const deleteMethod = this.methodsMapping[this.getEntityType(command.alarmType)]?.delete;

    return deleteMethod(command);
  }

  public createAlarmRule(command: CreateAlarmRuleCommand): Observable<string> {
    const createMethod = this.methodsMapping[this.getEntityType(command.alarmType)]?.create;

    return createMethod(command);
  }

  public updateAlarmRule(command: UpdateAlarmRuleCommand): Observable<string> {
    const updateMethod = this.methodsMapping[this.getEntityType(command.alarmType)]?.update;

    return updateMethod(command);
  }

  public isEquipmentAlarm(type: AlarmType) {
    return this.alarmTypesMapping[type] === EntityType.EQUIPMENT;
  }

  public isTransportAlarm(type: AlarmType) {
    return this.alarmTypesMapping[type] === EntityType.TRANSPORT;
  }

  private getEntityType(type: AlarmType): EntityType {
    return this.alarmTypesMapping[type];
  }

  private defineRequestParams({ page, size }: PageableRequestParams): AlarmRequestParams {
    return {
      // TODO: add filter params
      page,
      size,
    };
  }
}
